import { Fragment } from 'react';
import { TableCell, Paper, TableRow, Tooltip } from '@mui/material';
import styled from 'styled-components';
import Table from 'components/Table';
import { GenericTeamInterface, FilterType, HeadCell, CellRenderer } from 'types';
import { TeamsHistoricalUtilization } from 'ReportsModule/types';
import { useCsmAssignments } from 'CsmModule/hooks/useCsmAssignments';

const StyledPaper = styled(Paper)`
  margin-top: 1.5rem;
  overflow: hidden; /* Clip content to border radius */

  .csm-name {
    display: block;
    max-width: 200px;
  }
`;

type Props = {
  data: (GenericTeamInterface & { selected: boolean })[];
  headCells: HeadCell[];
  teamsUtilization: TeamsHistoricalUtilization;
  cellRenderer: CellRenderer<any>;
  filter: FilterType;
  isRequesting: boolean;
  useDefaultTable: boolean;
  hidePagination: boolean;
  handleRequestSort: (event: React.MouseEvent, property: string) => void;
  handleChangePage: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
  handleTeamRowClick: ({
    team,
    shouldOpenInAnotherTab,
  }: {
    team: GenericTeamInterface;
    shouldOpenInAnotherTab: boolean;
  }) => void;
  handleLinkClick: (url: string) => (e: React.MouseEvent<HTMLButtonElement>) => void;
  renderCustomPagination?: () => JSX.Element;
  csmAssignmentByTeam: ReturnType<typeof useCsmAssignments>['csmAssignmentByTeam'];
};

const StyledTableRow = styled(TableRow)`
  cursor: pointer;
  width: 100%;
  white-space: nowrap;
`;

const TeamsReportsTable = ({
  data,
  filter,
  headCells,
  cellRenderer,
  teamsUtilization,
  isRequesting,
  useDefaultTable,
  hidePagination,
  handleRequestSort,
  handleChangePage,
  handleTeamRowClick,
  handleLinkClick,
  renderCustomPagination,
  csmAssignmentByTeam,
}: Props) => {
  const { total, page, limit } = filter || {};

  const renderRows = () => {
    return data.map((data) => (
      <StyledTableRow
        hover
        selected={data.selected}
        onClick={(e) => {
          if (!e.defaultPrevented) {
            const shouldOpenInAnotherTab = e.ctrlKey || e.metaKey;
            handleTeamRowClick({ team: data, shouldOpenInAnotherTab });
          }
        }}
        key={data.id}
      >
        {headCells.map(({ id: headCellId }) => {
          const renderCell = cellRenderer[headCellId as keyof typeof cellRenderer];
          const teamId = data.id;

          return renderCell ? (
            <Fragment key={`${data.id}-${headCellId}`}>
              {renderCell({
                data,
                teamId,
                headCellId,
                handleLinkClick,
                teamsUtilization,
                csmAssignmentByTeam,
              })}
            </Fragment>
          ) : (
            <TableCell key={`${data.id}-${headCellId}`} align="left">
              {data[headCellId as keyof GenericTeamInterface]}
            </TableCell>
          );
        })}
      </StyledTableRow>
    ));
  };

  return (
    <StyledPaper>
      <Table
        isLoading={isRequesting}
        hidePagination={hidePagination}
        tableTitle={'Teams (Personal teams are not shown)'}
        useDefaultTable={useDefaultTable}
        renderRows={renderRows}
        headCells={headCells}
        onRequestSort={handleRequestSort}
        filter={filter}
        /* -------------------------------------------------------------------------- */
        renderCustomPagination={renderCustomPagination}
        rowsPerPageOptions={[limit]}
        count={total}
        rowsPerPage={limit}
        page={page}
        onPageChange={handleChangePage}
      />
    </StyledPaper>
  );
};

export default TeamsReportsTable;
